import BaseModel from '@/shared/models/Customer'
import i18n from '@/plugins/i18n'
import Plan from './Plan'
import Period from './Period'
import CancellationRequisition from './CancellationRequisition'
import _ from 'lodash'
import axios from '@/plugins/axios'
import store from '@/store'

export default class Customer extends BaseModel {
    resourceName() {
        return 'customers'
    }

    fields() {
        return [
            'id',
            'reseller_id',
            'erp_connection',
            'erp_contract_id',
            'erp_company_id',
            'erp_login_code',
            'trade_name',
            'legal_name',
            'cpf',
            'discount',
            'cnpj',
            'email',
            'phone',
            'status',
            'created_at',
            'recaptcha',
            'erp_data',
            'deleted_at',
            'plan',
            'modules',
            'cancellation_requisition',
            'category',
            'readable_status',
            'seller_id',
        ]
    }

    relationships() {
        return {
            plans: new Plan(),
            periods: new Period(),
            cancellationRequisition: new CancellationRequisition(),
        }
    }

    computed() {
        return {
            document(customer) {
                if (customer.cnpj) {
                    return customer.cnpj.replace(
                        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                        '$1.$2.$3/$4-$5',
                    )
                } else if (customer.cpf) {
                    return customer.cpf.replace(
                        /(\d{3})(\d{3})(\d{3})(\d{2})/,
                        '$1.$2.$3-$4',
                    )
                }

                return ''
            },

            formattedDeletedAt(customer) {
                return customer.deleted_at.isValid()
                    ? i18n.d(customer.deleted_at, 'datetime')
                    : null
            },

            activePlan(customer) {
                if (!customer.plans.length) return null

                return _.findLast(customer.plans, function(each) {
                    return !each.deleted_at.isValid()
                })
            },

            trialUntil(customer) {
                return customer.created_at.isValid()
                    ? i18n.d(customer.created_at.add(15, 'days'), 'short')
                    : null
            },

            deleted(customer) {
                return customer.deleted_at && customer.deleted_at.isValid()
            },
        }
    }

    async getPlans() {
        axios.get(`${this.resourceName()}/${this.id}/plans`).then(response => {
            return response.data
        })
    }

    async changePlan(plan) {
        return this.makePersistRequest({
            url: this.getSelfUrl() + '/change-plan',
            method: 'PUT',
            data: plan,
        })
    }

    get tableColumns() {
        return [
            {
                text: i18n.t('customer.index.table.erp-id'),
                value: 'erp_login_code',
            },
            {
                text: i18n.t('customer.index.table.contract_id'),
                value: 'erp_contract_id',
            },
            {
                text: i18n.t('customer.index.table.company'),
                value: 'company',
            },
            {
                text: i18n.t('customer.index.table.contacts'),
                sortable: false,
                value: 'contacts',
            },
            {
                text: i18n.t('customer.index.table.plan'),
                value: 'plan',
                sortable: false,
                hide: this.isWhitelabel || this.tabActive == 'trial',
            },
            {
                text: i18n.t('customer.index.table.status'),
                value: 'readable_status',
            },
            {
                text: 'Deletado',
                value: 'deleted_at',
                sortable: true,
                hide: this.tabActive != 'onlyTrashed',
            },
            {
                text: 'Trial vence em',
                value: 'trialUntil',
                sortable: false,
                hide: this.tabActive != 'trial',
            },
            {
                text: i18n.t('tables.actions'),
                value: 'action',
                sortable: false,
                hide: this.tabActive == 'onlyTrashed',
                align: 'end',
            },
        ]
    }

    get tableBaseQuery() {
        return this.with('plans')
    }

    get actions() {
        return [
            {
                type: 'menu',
                hide: !store.getters['reseller/billable'],
                actions: [
                    {
                        type: 'changePlan',
                        label: 'Selecionar Plano',
                    },
                    {
                        type: 'block',
                        label: i18n.t('resources.customer.actions.block'),
                        hide: this.blocked,
                    },
                    {
                        type: 'unblock',
                        label: i18n.t('resources.customer.actions.unblock'),
                        hide: !this.blocked,
                    },
                    {
                        type: 'requestCancellation',
                        label: i18n.t(
                            'resources.customer.actions.requestCancellation',
                        ),
                        disabled: !['active', 'blocked'].includes(this.status),
                    },
                ],
            },
            {
                type: 'menu',
                hide: !store.getters['reseller/isBusinessConsultant'],
                actions: [
                    {
                        type: 'createProposal',
                        label: i18n.t(
                            'resources.customer.actions.createProposal',
                        ),
                    },
                ],
            },
            {
                type: 'showErpData',
                icon: 'mdi-account-badge-outline',
                label: i18n.t('customer.index.texts.show-login-info'),
                hide: !(
                    this.erp_data &&
                    this.status != 'canceled' &&
                    !this.deleted
                ),
            },
            {
                type: 'show',
                to: {
                    name: 'customer.show',
                    params: { id: this.id },
                },
            },
            {
                type: 'edit',
                to: {
                    name: 'customer.edit',
                    params: { id: this.id },
                },
            },
            { type: 'delete' },
        ]
    }

    block() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/block`,
            method: 'PATCH',
        })
    }

    unblock() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/unblock`,
            method: 'PATCH',
        })
    }

    requestCancellation(reason) {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/request-cancellation`,
            method: 'POST',
            data: { reason: reason },
        })
    }
}
